import { Quote, Stethoscope, Star } from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import { useI18n } from "@/lib/i18n";
import { TESTIMONIAL_AVATARS } from "@/data/landing";

export default function Testimonials() {
  const { t } = useI18n();
  const items = t("testimonials.items") || [];
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 max-w-5xl">
          <Reveal>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-800 dark:text-emerald-400">
                {t("testimonials.kicker")}
              </div>
              <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight text-balance text-neutral-900 dark:text-neutral-50">
                {t("testimonials.h2")}
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div
              data-testid="trust-badge"
              className="inline-flex items-center gap-3 rounded-2xl border border-emerald-800/15 dark:border-emerald-400/20 bg-white dark:bg-neutral-900 px-5 py-3 max-w-sm"
            >
              <div className="h-10 w-10 rounded-2xl bg-emerald-800/10 dark:bg-emerald-400/10 inline-flex items-center justify-center">
                <Stethoscope className="h-5 w-5 text-emerald-800 dark:text-emerald-400" />
              </div>
              <div className="text-sm leading-snug">
                <div className="font-semibold text-neutral-900 dark:text-neutral-50">
                  {t("testimonials.badgeTitle")}
                </div>
                <div className="text-xs text-neutral-500">
                  {t("testimonials.badgeNote")}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((tx, i) => (
            <Reveal key={tx.name} delay={i * 0.08}>
              <article
                data-testid={`testimonial-${i}`}
                className="relative h-full rounded-3xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-7 sm:p-8 flex flex-col"
              >
                <Quote className="h-6 w-6 text-emerald-800/40 dark:text-emerald-400/50" />
                <p className="mt-4 text-[15px] sm:text-base text-neutral-800 dark:text-neutral-200 leading-relaxed">
                  &ldquo;{tx.quote}&rdquo;
                </p>

                <div className="mt-6 flex items-center gap-3 pt-5 border-t border-neutral-200 dark:border-neutral-800">
                  <img
                    src={TESTIMONIAL_AVATARS[i]}
                    alt={tx.name}
                    loading="lazy"
                    decoding="async"
                    className="h-11 w-11 rounded-full object-cover ring-2 ring-stone-100 dark:ring-neutral-800"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                      {tx.name}
                    </div>
                    <div className="text-xs text-neutral-500">{tx.role}</div>
                  </div>
                  <div className="flex items-center gap-0.5 text-orange-500">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star key={k} className="h-3.5 w-3.5 fill-current" />
                    ))}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
